// DefenderFileHashAllowlistManager — admin page at /settings/defender-file-hash-allowlist.
// Backs the Defender pass-through rule's `skip_if_in_file_hash_allowlist` tier (v1.78.0):
// a GLOBAL cross-tenant allowlist of (file hash, threat-family) pairs. When the rule
// toggle is on, a Defender alert is suppressed if any file-evidence hash (sha256/sha1/md5)
// matches an enabled entry for the alert's threat family (or a '*' = any-threat entry).
// A known-malicious IP still alerts. Read-open, admin-write. Soft-delete (enabled=false).
// Every write bumps a __version sentinel the detection pipeline polls (~30s).
// Identity = (PartitionKey = hash, RowKey = normalized threat slug or '*') — immutable.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  FingerPrintIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchDefenderFileHashAllowlist,
  fetchDefenderFileHashAllowlistSentinelHealth,
  addDefenderFileHashEntry,
  updateDefenderFileHashEntry,
  removeDefenderFileHashEntry,
  reconcileDefenderFileHashAllowlistSentinel,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { hashTypeTone } from './ui/badgeTones';
import Pagination from './Pagination';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

// Threat-family normalizer — MUST round-trip with the pipeline EXACTLY
// (_detection_lib.py::_normalize_threat_family). '*' = any threat.
export function normalizeThreatFamily(raw) {
  const s = (raw ?? '').trim();
  if (s === '*') return '*';
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function isHexHash(h) { return /^[0-9a-f]+$/i.test((h || '').trim()); }
function hashTypeForLength(h) {
  const n = (h || '').trim().length;
  if (n === 64) return 'sha256';
  if (n === 40) return 'sha1';
  if (n === 32) return 'md5';
  return '';
}

const rowId = (r) => `${r.hash}|${r.threat}`;

export default function DefenderFileHashAllowlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [threatFilter, setThreatFilter] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);

  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  const [formHash, setFormHash] = useState('');
  const [formThreat, setFormThreat] = useState('');
  const [formFile, setFormFile] = useState('');
  const [formSigner, setFormSigner] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editThreatDisplay, setEditThreatDisplay] = useState('');
  const [editFile, setEditFile] = useState('');
  const [editSigner, setEditSigner] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [sortKey, setSortKey] = useState('threat');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Hash, Threat, File, Signer, Enabled, Notes, Added, Added By, Actions
  const [colWidths, setColWidths] = useState([300, 180, 200, 160, 100, 220, 120, 160, 120]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDefenderFileHashAllowlist();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load file-hash allowlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await fetchDefenderFileHashAllowlistSentinelHealth();
      setHealth(h);
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    load();
    loadHealth();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  const threatOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.threat) set.add(r.threat); });
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let r = rows;
    if (!showDisabled) r = r.filter((x) => x.enabled !== false);
    if (threatFilter) r = r.filter((x) => x.threat === threatFilter);
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.hash && x.hash.toLowerCase().includes(q)) ||
        (x.fileName && x.fileName.toLowerCase().includes(q)) ||
        (x.threat && x.threat.toLowerCase().includes(q)) ||
        (x.threatDisplayName && x.threatDisplayName.toLowerCase().includes(q)) ||
        (x.signer && x.signer.toLowerCase().includes(q)) ||
        (x.notes && x.notes.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, showDisabled, threatFilter, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, threatFilter, showDisabled, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  // Live add-form derivations
  const hashTrim = formHash.trim().toLowerCase();
  const derivedHashType = hashTypeForLength(hashTrim);
  const hashLooksValid = isHexHash(hashTrim) && !!derivedHashType;
  const derivedThreatSlug = normalizeThreatFamily(formThreat);
  const threatIsWildcard = derivedThreatSlug === '*';
  const threatLooksValid = derivedThreatSlug.length > 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!hashTrim) { setAddError('Hash is required.'); return; }
    if (!isHexHash(hashTrim)) { setAddError('Hash must be hexadecimal (0-9a-f only).'); return; }
    if (!derivedHashType) { setAddError(`Hash length ${hashTrim.length} is invalid — expected 64 (sha256), 40 (sha1), or 32 (md5).`); return; }
    if (!threatLooksValid) { setAddError('Threat family normalizes to empty — enter a family or the literal "*".'); return; }
    if (formNotes && formNotes.length > 2000) { setAddError('Notes exceeds 2000 chars.'); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addDefenderFileHashEntry({
        hash: hashTrim,
        threat: formThreat.trim(),
        threatDisplayName: formThreat.trim(),
        fileName: formFile.trim(),
        signer: formSigner.trim(),
        notes: formNotes.trim(),
        enabled: true,
      });
      setRows((prev) => {
        const ix = prev.findIndex((r) => rowId(r) === rowId(created));
        if (ix >= 0) { const next = [...prev]; next[ix] = created; return next; }
        return [created, ...prev];
      });
      setFormHash(''); setFormThreat(''); setFormFile(''); setFormSigner(''); setFormNotes('');
      loadHealth();
    } catch (err) {
      setAddError(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(rowId(row));
    setEditThreatDisplay(row.threatDisplayName || '');
    setEditFile(row.fileName || '');
    setEditSigner(row.signer || '');
    setEditNotes(row.notes || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditError(null); };

  const handleSaveEdit = async (row) => {
    if (editNotes && editNotes.length > 2000) { setEditError('Notes exceeds 2000 chars.'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateDefenderFileHashEntry(row.hash, row.threat, {
        threatDisplayName: editThreatDisplay.trim(),
        fileName: editFile.trim(),
        signer: editSigner.trim(),
        notes: editNotes.trim(),
      });
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, ...updated } : x)));
      cancelEdit();
      loadHealth();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(rowId(row));
    try {
      const updated = await updateDefenderFileHashEntry(row.hash, row.threat, { enabled: !row.enabled });
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, ...updated } : x)));
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to toggle entry');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeDefenderFileHashEntry(row.hash, row.threat);
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, enabled: false } : x)));
      setConfirmRemove(null);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to disable entry');
    } finally {
      setRemoving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileDefenderFileHashAllowlistSentinel();
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  const cols = [
    { label: 'Hash',     sortKey: 'hash' },
    { label: 'Threat',   sortKey: 'threat' },
    { label: 'File',     sortKey: 'fileName' },
    { label: 'Signer',   sortKey: 'signer' },
    { label: 'Enabled',  sortKey: 'enabled' },
    { label: 'Notes',    sortKey: null },
    { label: 'Added',    sortKey: 'addedUtc' },
    { label: 'Added By', sortKey: 'addedBy' },
    { label: 'Actions',  sortKey: null },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <FingerPrintIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Defender File-Hash Allowlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Global <strong>(file hash, threat-family)</strong> pairs that suppress the Microsoft Defender pass-through
          rule. When a Defender alert's file-evidence hash (sha256 / sha1 / md5) matches an enabled entry for the
          alert's threat family — or a <span className="font-mono">*</span> any-threat entry — the alert is dropped.
          Pairing the hash with the threat family means the same file flagged as a <em>different</em> threat still
          alerts, and a <strong>known-malicious IP still alerts</strong> regardless. Use this to clear vendor
          false-positive waves (e.g. Defender mass-flagging a signed installer).
        </p>
      </div>

      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30&nbsp;s.
          </span>
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} className="ml-auto underline hover:no-underline text-amber-200">
              Reconcile now
            </button>
          )}
        </div>
      )}

      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.</span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add (Hash, Threat) Entry</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">File hash (sha256 / sha1 / md5)</label>
                <input
                  type="text"
                  placeholder="lowercase hex…"
                  value={formHash}
                  onChange={(e) => setFormHash(e.target.value)}
                  className="input-field h-10 py-0 font-mono text-xs"
                  required
                />
                {hashTrim && (
                  hashLooksValid ? (
                    <p className="text-xs text-gray-500 mt-1">Detected type: <Badge tone={hashTypeTone(derivedHashType)}>{derivedHashType}</Badge></p>
                  ) : (
                    <p className="text-xs text-red-400 mt-1">
                      {isHexHash(hashTrim) ? `Length ${hashTrim.length} is invalid — expected 64 / 40 / 32.` : 'Not valid hex (0-9a-f only).'}
                    </p>
                  )
                )}
              </div>
              <div className="w-full lg:w-64">
                <label className="block text-xs text-gray-500 mb-1">Threat family (or <span className="font-mono">*</span>)</label>
                <input
                  type="text"
                  placeholder="e.g. Wacatac"
                  value={formThreat}
                  onChange={(e) => setFormThreat(e.target.value)}
                  className="input-field h-10 py-0"
                  required
                />
                {formThreat.trim() && (
                  <p className="text-xs text-gray-500 mt-1">RowKey: <span className="font-mono text-gray-400">{derivedThreatSlug || '(empty — rejected)'}</span></p>
                )}
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">File name (optional)</label>
                <input type="text" placeholder="e.g. ScreenConnect.ClientSetup.msi" value={formFile} onChange={(e) => setFormFile(e.target.value)} className="input-field h-10 py-0" />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Signer (optional)</label>
                <input type="text" placeholder="e.g. ConnectWise, LLC" value={formSigner} onChange={(e) => setFormSigner(e.target.value)} className="input-field h-10 py-0" />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                <input type="text" placeholder="why this is benign" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} className="input-field h-10 py-0" />
              </div>
              <div className="flex-shrink-0">
                <button type="submit" disabled={adding || !hashLooksValid || !threatLooksValid} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Entry
                </button>
              </div>
            </div>
            {threatIsWildcard && (
              <p className="text-xs text-amber-300/90 flex items-start gap-1.5">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>A <span className="font-mono">*</span> entry trusts this file for <strong>ANY</strong> Defender verdict — the file will never alert through the pass-through rule again (unless seen from a known-malicious IP). Prefer a specific threat family.</span>
              </p>
            )}
            <p className="text-xs text-gray-600">
              Identity is the (hash, threat-slug) pair. Re-adding the same pair is an idempotent upsert. To change
              the hash or threat, disable the old entry and add a new one.
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or disable entries.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by hash, threat, file, signer, notes…"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select value={threatFilter} onChange={(e) => setThreatFilter(e.target.value)} className="input-field w-44">
            <option value="">All threats</option>
            {threatOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
            />
            Show disabled
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} disabled={reconciling} className="btn-secondary flex items-center gap-1.5 text-xs" title="Force-bump the sentinel (recovery)">
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <span>{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {cols.map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === cols.length - 1 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}>
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading allowlist…</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <FingerPrintIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No file-hash allowlist entries</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || threatFilter || !showDisabled ? 'Try adjusting your filters.' : isAdmin ? 'Add an entry above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const isEditing = editingId === rowId(r);
                const isEnabled = r.enabled !== false;
                const isWildcard = r.threat === '*';
                return (
                <tr key={rowId(r)} className={`hover:bg-gray-800/50 transition-colors ${isEnabled ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 truncate overflow-hidden" title={r.hash}>
                    <div className="flex items-center gap-2">
                      <Badge tone={hashTypeTone(r.hashType)}>{r.hashType || '?'}</Badge>
                      <span className="font-mono text-xs text-gray-300 truncate">{r.hash}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 truncate overflow-hidden" title={`${r.threatDisplayName || ''} (${r.threat})`}>
                    {isEditing ? (
                      <input type="text" value={editThreatDisplay} onChange={(e) => setEditThreatDisplay(e.target.value)} className="input-field w-full text-xs py-1" placeholder="display name" />
                    ) : (
                      <div className="flex flex-col">
                        {isWildcard ? (
                          <Badge tone="warning">{'* any threat'}</Badge>
                        ) : (
                          <>
                            <span className="text-gray-300">{r.threatDisplayName || r.threat}</span>
                            <span className="font-mono text-[10px] text-gray-600 truncate">{r.threat}</span>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.fileName || ''}>
                    {isEditing ? (
                      <input type="text" value={editFile} onChange={(e) => setEditFile(e.target.value)} className="input-field w-full text-xs py-1" />
                    ) : (r.fileName || '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.signer || ''}>
                    {isEditing ? (
                      <input type="text" value={editSigner} onChange={(e) => setEditSigner(e.target.value)} className="input-field w-full text-xs py-1" />
                    ) : (r.signer || '—')}
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button onClick={() => handleToggleEnabled(r)} disabled={togglingId === rowId(r)} title={isEnabled ? 'Click to disable' : 'Click to enable'} className="focus:outline-none">
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                      </button>
                    ) : (
                      <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input type="text" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="input-field w-full text-xs py-1" />
                    ) : (r.notes || '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.addedUtc)}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>{r.addedBy || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleSaveEdit(r)} disabled={savingEdit} className="text-gray-600 hover:text-green-400 transition-colors p-1" title="Save">
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === rowId(r) ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Disabling…' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit context fields (hash + threat are immutable — disable and re-add to change them)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(rowId(r))} disabled={!isEnabled} className="text-gray-600 hover:text-red-400 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed" title={isEnabled ? 'Disable (soft-delete)' : 'Already disabled'}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
